import React, {forwardRef, useEffect, useRef} from 'react'
import Checkbox from '@material-ui/core/Checkbox'
import PropTypes from 'prop-types'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableFooter from '@material-ui/core/TableFooter'
import TableHead from '@material-ui/core/TableHead'
import TablePagination from '@material-ui/core/TablePagination'
import TableRow from '@material-ui/core/TableRow'
import TableSortLabel from '@material-ui/core/TableSortLabel'
import {usePagination, useRowSelect, useSortBy, useTable,} from 'react-table'
import ExpandedTablePaginationActions from "./expandedTablePaginationActions";
import {makeStyles} from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import Paper from "@material-ui/core/Paper";

const useStyles = makeStyles(theme => ({
    theTable: {
        '& .MuiTableCell-root': {
            padding: theme.spacing(0, 1, 0, 1),
        },
        overflowY: 'scroll',
    },
}));

const IndeterminateCheckbox = forwardRef(
    ({indeterminate, ...rest}, ref) => {
        const defaultRef = useRef()
        const resolvedRef = ref || defaultRef

        useEffect(() => {
            resolvedRef.current.indeterminate = indeterminate
        }, [resolvedRef, indeterminate])

        // console.log('resolvedRef', rest);
        return (
            <>
                <Checkbox ref={resolvedRef} {...rest} />
            </>
        )
    }
)

const ExpandedTable = ({
                           columns,
                           data,
                           dataCount,
                           getData,
                           pageSize,
                           pageIndex,
                           getRowId,
                           setSelected,
                           testFieldName,
                           skipPageReset,
                           setSkipPageReset,
                           hideRowsPerPage,
                           height,
                       }) => {
    const classes = useStyles();

    const {
        getTableProps,
        headerGroups,
        prepareRow,
        page,
        setPageSize,
        selectedFlatRows,
        state: {selectedRowIds, sortBy},
    } = useTable(
        {
            columns,
            data,
            manualSortBy: true,
            disableSortRemove: true,
            autoResetPage: !skipPageReset,
            autoResetSelectedRows: !skipPageReset,
            autoResetSortBy: !skipPageReset,
            getRowId: row => getRowId(row),
            getData,
            initialState: {
                sortBy: [{id: 'updatedAt', desc: true}]
            }
        },
        useSortBy,
        usePagination,
        useRowSelect,
        hooks => {
            if (setSelected) {
                hooks.allColumns.push(columns => [
                    {
                        id: 'selection',
                        Header: ({getToggleAllRowsSelectedProps}) => (
                            <div>
                                <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
                            </div>
                        ),
                        Cell: ({row}) => (
                            <div>
                                <IndeterminateCheckbox
                                    name={testFieldName && row.original[testFieldName].replace(/\s+/g, '-').toLowerCase() === '___test' ? 'checkTest' : ''} {...row.getToggleRowSelectedProps()} />
                            </div>
                        ),
                    },
                    ...columns,
                ])
            }
        }
    )

    useEffect(() => {
        console.log('useEffect sortBy', pageIndex, pageSize);
        getData(pageIndex, pageSize, sortBy)
    }, [sortBy])

    useEffect(() => {
        console.log('useEffect data', pageIndex, pageSize);
        if (!skipPageReset) setSkipPageReset(true);
    }, [data]);

    useEffect(() => {
        if (setSelected) {
            console.log('selectedFlatRows selectedFlatRows', pageIndex, pageSize, setSelected);
            const rows = selectedFlatRows.map(selectedFlatRow => selectedFlatRow.original);
            setSelected(Object.keys(selectedRowIds).sort(), rows);
        }
    }, [selectedFlatRows, selectedFlatRows]);

    const handleChangePage = (event, newPage) => {
        console.log('handleChangePage', newPage, pageSize);
        getData(newPage, pageSize, sortBy);
    }

    const handleChangeRowsPerPage = event => {
        const newPageSize = Number(event.target.value)
        if (pageSize !== newPageSize) setPageSize(newPageSize);
        console.log('handleChangeRowsPerPage', pageIndex, newPageSize, selectedRowIds);
        getData(pageIndex, newPageSize, sortBy);
    }

    return (
        <TableContainer className={classes.theTable} component={Paper} style={{height: height}} >
            <Table {...getTableProps()}>
                <TableHead>
                    {headerGroups.map(headerGroup => (
                        <TableRow {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map(column => (
                                <TableCell
                                    {...(column.id === 'selection'
                                        ? column.getHeaderProps()
                                        : column.getHeaderProps(column.getSortByToggleProps()))}
                                >
                                    {column.render('Header')}
                                    {column.id !== 'selection' ? (
                                        <TableSortLabel
                                            active={column.isSorted}
                                            // react-table has a unsorted state which is not treated here
                                            direction={column.isSortedDesc ? 'desc' : 'asc'}
                                        />
                                    ) : null}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableHead>
                <TableBody>
                    {page.map((row, i) => {
                        prepareRow(row)
                        return (
                            <TableRow {...row.getRowProps()}>
                                {row.cells.map(cell => {
                                    return (
                                        <TableCell {...cell.getCellProps()}>
                                            {cell.render('Cell')}
                                        </TableCell>
                                    )
                                })}
                            </TableRow>
                        )
                    })}
                </TableBody>
                <TableFooter>
                    <TableRow key={"footer-row"}>
                        <TablePagination
                            rowsPerPageOptions={hideRowsPerPage ? [] : [10, 25, 50, 100]}
                            colSpan={3}
                            count={dataCount}
                            rowsPerPage={pageSize}
                            page={pageIndex}
                            SelectProps={{
                                inputProps: {'aria-label': 'rows per page'},
                                native: true,
                            }}
                            onChangePage={handleChangePage}
                            onChangeRowsPerPage={handleChangeRowsPerPage}
                            ActionsComponent={ExpandedTablePaginationActions}
                        />
                    </TableRow>
                </TableFooter>
            </Table>
        </TableContainer>
    )
}

ExpandedTable.propTypes = {
    columns: PropTypes.array.isRequired,
    data: PropTypes.array.isRequired,
    dataCount: PropTypes.number.isRequired,
    getData: PropTypes.func.isRequired,
    pageIndex: PropTypes.number.isRequired,
    pageSize: PropTypes.number.isRequired,
    skipPageReset: PropTypes.bool.isRequired,
    setSkipPageReset: PropTypes.func.isRequired,
    getRowId: PropTypes.func.isRequired,
}

export default ExpandedTable
